import { type ReactNode } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { cn } from '@/utils/cn';
import type { Payment } from '../types';
import { type PaymentMode, filterPendingByMode } from '../utils/payment-mode';
import { usePendingPayments } from '../hooks/use-pending-payments';
import { usePaidPayments } from '../hooks/use-paid-payments';
import { usePaidConfirm } from '../hooks/use-paid-confirm';
import { usePaymentDrag } from '../hooks/use-payment-drag';
import { PaymentCardOverlay } from './payment-card-overlay';
import { PaymentConfirmModal } from './payment-confirm-modal';
import { PendingCol, PaidCol } from './payment-columns';

function usePaymentsData(mode: PaymentMode) {
  const { data: pending, isLoading: lp } = usePendingPayments();
  const { data: paid, isLoading: lh } = usePaidPayments();
  return {
    pending: filterPendingByMode(pending ?? [], mode),
    paid: paid ?? [],
    lp,
    lh,
  };
}

const BOARD =
  'flex h-[calc(100dvh-200px)] gap-sm overflow-x-auto pb-sm scrollbar-thin snap-x snap-mandatory sm:snap-none';

function usePointerSensors() {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );
}

// While confirming, show the card optimistically in "Pagado" (status forced to
// 'paid') and remove it from "Pendiente"; commit/revert happens on
// confirm/cancel in usePaidConfirm.
function shownLists(
  confirming: Payment | null,
  pending: Payment[],
  paid: Payment[]
) {
  if (!confirming) return { pendingShown: pending, paidShown: paid };
  return {
    pendingShown: pending.filter((p) => p.id !== confirming.id),
    paidShown: [{ ...confirming, status: 'paid' as const }, ...paid],
  };
}

type BoardProps = {
  active: Payment | null;
  pendingShown: Payment[];
  paidShown: Payment[];
  lp: boolean;
  lh: boolean;
};

function Board(p: BoardProps): ReactNode {
  return (
    <>
      <div className={cn(BOARD, p.active && 'cursor-grabbing')}>
        <PendingCol items={p.pendingShown} loading={p.lp} />
        <PaidCol items={p.paidShown} loading={p.lh} />
      </div>
      <DragOverlay dropAnimation={null}>
        {p.active && <PaymentCardOverlay payment={p.active} />}
      </DragOverlay>
    </>
  );
}

export function PaymentsKanban({ mode }: { mode: PaymentMode }): ReactNode {
  const { pending, paid, lp, lh } = usePaymentsData(mode);
  const confirm = usePaidConfirm();
  const { active, onStart, onEnd, onCancel } = usePaymentDrag(confirm.onDrop);
  const sensors = usePointerSensors();
  const lists = shownLists(confirm.confirming, pending, paid);
  return (
    <DndContext
      sensors={sensors}
      onDragStart={onStart}
      onDragEnd={onEnd}
      onDragCancel={onCancel}
    >
      <Board active={active} {...lists} lp={lp} lh={lh} />
      <PaymentConfirmModal
        payment={confirm.confirming}
        onConfirm={confirm.onConfirm}
        onCancel={confirm.onCancel}
        isPending={confirm.isPending}
      />
    </DndContext>
  );
}
